import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { loadBuildingsForSelector } from '../../../api';
import {
  BuildingLedgerDetailReportResponse,
  BuildingLedgerDetailSection,
  BuildingLedgerDetailSectionKey,
  incomeExpenseApi,
  ProjectBankAccount,
} from '../../../api/incomeExpenseApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  selectBuilding,
  shouldShowBuildingSelector,
} from '../../../utils/buildingSelection';
import BillingPeriodMonthPicker from '../../../components/common/BillingPeriodMonthPicker';
import { getCurrentBillingPeriodValue } from '../../../utils/billingPeriodPicker';
import {
  billingPeriodToFilenameKy,
  downloadExcel,
  projectAccountLabelForFilename,
  sanitizeExportFilename,
} from '../../../utils/excelExport';

export interface IncomeExpenseBuildingLedgerDetailTabHandle {
  exportReport: () => Promise<void>;
}

const SECTION_ORDER: BuildingLedgerDetailSectionKey[] = [
  'CASH_IN',
  'CASH_OUT',
  'BANK_IN',
  'BANK_OUT',
];

const SECTION_FILENAME_SLUG: Record<BuildingLedgerDetailSectionKey, string> = {
  CASH_IN: 'thu-tien-mat',
  CASH_OUT: 'chi-tien-mat',
  BANK_IN: 'thu-ngan-hang',
  BANK_OUT: 'chi-ngan-hang',
};

function buildChiTietThuChiExportFilename(options: {
  scope: 'all' | BuildingLedgerDetailSectionKey;
  buildingName: string;
  collectionPeriod: string;
  projectAccountName: string;
}): string {
  const scopePart =
    options.scope === 'all' ? 'tat-ca-4-muc' : SECTION_FILENAME_SLUG[options.scope];
  const buildingPart = sanitizeExportFilename(options.buildingName) || 'chung-cu';
  const kyPart = billingPeriodToFilenameKy(options.collectionPeriod);
  const tkPart = projectAccountLabelForFilename(options.projectAccountName);
  return sanitizeExportFilename(
    `chi-tiet-thu-chi_${scopePart}_${buildingPart}_${kyPart}_${tkPart}`,
  );
}

const LEDGER_DETAIL_EXPORT_HEADERS = [
  'Ngày',
  'Loại',
  'Tên phiếu',
  'Căn hộ',
  'Tài khoản dự án',
  'Số tiền',
  'Diễn giải',
  'Người thực hiện',
];

function appendSectionExportRows(
  section: BuildingLedgerDetailSection,
  sheetRows: (string | number)[][],
  boldRowIndices: number[],
) {
  boldRowIndices.push(sheetRows.length);
  sheetRows.push([section.title, '', '', '', '', section.totalAmount, '', '']);
  for (const group of section.groups) {
    boldRowIndices.push(sheetRows.length);
    sheetRows.push([`  ${group.typeName}`, '', '', '', '', group.totalAmount, '', '']);
    for (const line of group.lines) {
      sheetRows.push([
        formatDateTime(line.actualDate),
        line.typeName,
        line.voucherReceiptRef
          ? `${line.voucherName} — ${line.voucherReceiptRef}`
          : line.voucherName,
        formatApartment(line.apartmentCode, line.apartmentCustomerCode),
        line.projectAccountName,
        line.amount,
        line.description,
        line.performerName || '',
      ]);
    }
  }
}

function downloadBuildingLedgerDetailExcel(options: {
  report: BuildingLedgerDetailReportResponse;
  buildingName: string;
  selectedProjectAccountName: string;
  sections: BuildingLedgerDetailSection[];
  reportTitle: string;
  filenameBase: string;
}) {
  const sheetRows: (string | number)[][] = [];
  const boldRowIndices: number[] = [];
  for (const section of options.sections) {
    appendSectionExportRows(section, sheetRows, boldRowIndices);
    if (options.sections.length > 1) {
      sheetRows.push([]);
    }
  }

  const metaRows: (string | number)[][] = [
    [options.reportTitle],
    ['Tòa nhà', options.buildingName],
    ['Kỳ báo cáo', options.report.collectionPeriod],
    ...(options.selectedProjectAccountName
      ? [['Tài khoản dự án', options.selectedProjectAccountName] as (string | number)[]]
      : []),
    ['Từ ngày', options.report.dateFrom],
    ['Đến ngày', options.report.dateTo],
    [],
  ];

  const filename = sanitizeExportFilename(options.filenameBase);
  downloadExcel('Chi tiết', LEDGER_DETAIL_EXPORT_HEADERS, sheetRows, `${filename}.xlsx`, {
    metaRows,
    boldMetaRowIndices: [0],
    boldRowIndices,
    rightAlignColumnIndices: [5],
  });
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(value)) + 'đ';

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatApartment = (code: string | null, customerCode: string | null) => {
  if (!code) return '—';
  return customerCode ? `${code} (${customerCode})` : code;
};

function filterReportByProjectAccount(
  report: BuildingLedgerDetailReportResponse,
  projectBankAccountId: string,
): BuildingLedgerDetailReportResponse {
  if (!projectBankAccountId) return report;
  const sections: BuildingLedgerDetailSection[] = report.sections.map((section) => {
    const groups = section.groups
      .map((group) => {
        const lines = group.lines.filter(
          (line) => line.projectBankAccountId === projectBankAccountId,
        );
        if (!lines.length) return null;
        const totalAmount = lines.reduce((sum, line) => sum + line.amount, 0);
        return { typeName: group.typeName, totalAmount, lines };
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
    const totalAmount = groups.reduce((sum, group) => sum + group.totalAmount, 0);
    return { ...section, groups, totalAmount };
  });
  return { ...report, sections };
}

export const IncomeExpenseBuildingLedgerDetailTab = forwardRef<
  IncomeExpenseBuildingLedgerDetailTabHandle
>(function IncomeExpenseBuildingLedgerDetailTab(_props, ref) {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [collectionPeriod, setCollectionPeriod] = useState(getCurrentBillingPeriodValue());
  const [report, setReport] = useState<BuildingLedgerDetailReportResponse | null>(null);
  const [projectAccounts, setProjectAccounts] = useState<ProjectBankAccount[]>([]);
  const [projectBankAccountId, setProjectBankAccountId] = useState('');
  const [activeSection, setActiveSection] = useState<BuildingLedgerDetailSectionKey>('CASH_IN');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  useEffect(() => {
    if (!buildingId) {
      setProjectAccounts([]);
      setProjectBankAccountId('');
      return;
    }
    incomeExpenseApi
      .listProjectBankAccounts(buildingId)
      .then((accounts) => {
        const active = (accounts || []).filter((a) => a.isActive);
        active.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
        setProjectAccounts(active);
      })
      .catch(() => setProjectAccounts([]));
    setProjectBankAccountId('');
  }, [buildingId]);

  const loadReport = useCallback(async () => {
    if (!buildingId || !collectionPeriod) return;
    setLoading(true);
    try {
      const data = await incomeExpenseApi.getBuildingLedgerDetailReport({
        buildingId,
        collectionPeriod,
      });
      setReport(data);
    } catch {
      toast.error('Không thể tải chi tiết thu chi');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [buildingId, collectionPeriod]);

  useEffect(() => {
    if (!buildingId) return;
    const timer = setTimeout(loadReport, 200);
    return () => clearTimeout(timer);
  }, [loadReport, buildingId]);

  const buildingName = useMemo(
    () => buildings.find((b) => b.id === buildingId)?.name || 'chung-cu',
    [buildings, buildingId],
  );

  const filteredReport = useMemo(() => {
    if (!report) return null;
    return filterReportByProjectAccount(report, projectBankAccountId);
  }, [report, projectBankAccountId]);

  const selectedProjectAccountName = useMemo(() => {
    if (!projectBankAccountId) return '';
    return projectAccounts.find((a) => a.id === projectBankAccountId)?.name ?? '';
  }, [projectAccounts, projectBankAccountId]);

  const sectionMap = useMemo(() => {
    const map = new Map<BuildingLedgerDetailSectionKey, BuildingLedgerDetailReportResponse['sections'][number]>();
    for (const section of filteredReport?.sections ?? []) {
      map.set(section.key, section);
    }
    return map;
  }, [filteredReport]);

  const active = sectionMap.get(activeSection);

  const exportSections = useCallback(
    (sectionKeys: BuildingLedgerDetailSectionKey[]) => {
      if (!filteredReport) {
        toast.error('Không có dữ liệu để xuất');
        return;
      }
      const sections = sectionKeys
        .map((key) => sectionMap.get(key))
        .filter((section): section is BuildingLedgerDetailSection => Boolean(section));
      const hasExportData = sections.some((section) =>
        section.groups.some((group) => group.lines.length > 0),
      );
      if (!hasExportData) {
        toast.error('Không có dữ liệu để xuất');
        return;
      }

      const isFullReport = sectionKeys.length === SECTION_ORDER.length;
      const filenameBase = buildChiTietThuChiExportFilename({
        scope: isFullReport ? 'all' : sectionKeys[0],
        buildingName,
        collectionPeriod: filteredReport.collectionPeriod,
        projectAccountName: selectedProjectAccountName,
      });

      downloadBuildingLedgerDetailExcel({
        report: filteredReport,
        buildingName,
        selectedProjectAccountName,
        sections,
        reportTitle: isFullReport ? 'CHI TIẾT THU CHI' : sections[0].title,
        filenameBase,
      });
      toast.success('Đã xuất báo cáo');
    },
    [filteredReport, buildingName, selectedProjectAccountName, sectionMap],
  );

  const exportReport = useCallback(async () => {
    exportSections(SECTION_ORDER);
  }, [exportSections]);

  useImperativeHandle(ref, () => ({ exportReport }), [exportReport]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-3">
          {shouldShowBuildingSelector(buildings) && (
            <select
              value={buildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
              disabled={!needsBuildingSelection(buildings)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[160px]"
            >
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">Kỳ báo cáo</span>
            <BillingPeriodMonthPicker
              value={collectionPeriod}
              onChange={setCollectionPeriod}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[200px]">
            <span className="text-xs text-gray-500">Tài khoản dự án</span>
            <select
              value={projectBankAccountId}
              onChange={(e) => setProjectBankAccountId(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">Tất cả tài khoản</option>
              {projectAccounts.map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {SECTION_ORDER.map((key) => {
          const section = sectionMap.get(key);
          const title = section?.title ?? key;
          const total = section?.totalAmount ?? 0;
          const isActive = activeSection === key;
          const lineCount = section?.groups.reduce((n, g) => n + g.lines.length, 0) ?? 0;
          return (
            <div
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => setActiveSection(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveSection(key);
                }
              }}
              className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${
                isActive
                  ? 'border-cyan-600 bg-cyan-50 ring-1 ring-cyan-600'
                  : 'border-gray-200 bg-white hover:border-cyan-300'
              }`}
            >
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {title}
              </div>
              <div className="text-lg font-semibold text-gray-900 mt-1">{formatMoney(total)}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {(section?.groups.length ?? 0)} loại · {lineCount} phiếu
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  exportSections([key]);
                }}
                disabled={lineCount === 0}
                className="mt-2 text-xs font-medium text-cyan-700 hover:text-cyan-900 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Xuất báo cáo
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Đang tải...</div>
        ) : !active || active.groups.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Không có phiếu trong mục này</div>
        ) : (
          <div className="divide-y">
            {active.groups.map((group) => (
              <div key={group.typeName} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{group.typeName}</h4>
                  <span className="text-sm font-medium text-cyan-700">{formatMoney(group.totalAmount)}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-xs text-gray-500 border-b">
                      <tr>
                        <th className="py-2 pr-3 text-left">Ngày</th>
                        <th className="py-2 pr-3 text-left">Tên phiếu</th>
                        <th className="py-2 pr-3 text-left">Căn hộ</th>
                        <th className="py-2 pr-3 text-left">Tài khoản</th>
                        <th className="py-2 pr-3 text-right">Số tiền</th>
                        <th className="py-2 text-left">Diễn giải</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.lines.map((line) => (
                        <tr key={`${line.entryId}-${line.amount}`} className="border-t border-gray-100">
                          <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(line.actualDate)}</td>
                          <td className="py-2 pr-3">{line.voucherName}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            {formatApartment(line.apartmentCode, line.apartmentCustomerCode)}
                          </td>
                          <td className="py-2 pr-3">{line.projectAccountName}</td>
                          <td className="py-2 pr-3 text-right whitespace-nowrap">{formatMoney(line.amount)}</td>
                          <td className="py-2 text-gray-600">{line.description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default IncomeExpenseBuildingLedgerDetailTab;
